import { TooltipButton } from "@/components/atoms/TooltipButton";
import DecisionModal from '@/components/custom/InfoPopups/DecisionModal';
import ErrorPopup from '@/components/custom/InfoPopups/ErrorPopup';
import SuccessPopup from '@/components/custom/InfoPopups/SuccessPopup';
import LoaderWithBackdrop from '@/components/custom/LoaderWithBackdrop';
import { NavigationBarBreadCrumbs } from '@/components/molecules/BackNav';
import { SearchInput } from '@/components/molecules/SearchInput';
import { Button } from '@/components/ui/button';
import { crmSystem } from '@/constants/crm/_shared/systems';
import { CrmSystemCheckboxSelectSanitized } from '@/features/crm/components/organisms/CrmSystemCheckboxSelectSanitized';
import { crmRegistry } from '@/features/crm_system/lib/_core/crmRegistry';
import { CrmSystemIds } from '@/features/crm_system/lib/_core/crmSystem/crmSystem.types.gen';
import { useGetOrganizationCrmSystems } from "@/features/organization/hooks/utils/useGetOrganizationCrmSystems/useGetOrganizationCrmSystems";
import { EditQuestionModal } from '@/features/question/components/organisms/EditQuestionModal';
import { QuestionFormSchemaType } from '@/features/question/components/organisms/QuestionForm';
import { AddBankQuestionModal } from '@/features/question_bank/components/organisms/AddBankQuestionModal';
import { useAddBankQuestion } from '@/features/question_bank/hooks/useMutation/useAddBankQuestion';
import { useDeleteBankQuestion } from '@/features/question_bank/hooks/useMutation/useDeleteBankQuestion';
import { useEditBankQuestion } from '@/features/question_bank/hooks/useMutation/useEditBankQuestion';
import { useGetQuestionBankQuestions } from '@/features/question_bank/hooks/useQuery/useGetQuestionBankQuestions';
import { TablesInsert } from '@/lib/supabase/supabase_types_override';
import { cn } from '@/lib/utils';
import { useOrganizationStore } from '@/lib/zustand/useStore';
import { captureException } from '@sentry/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { formatDate } from 'date-fns';
import { Loader, Pencil, Trash2 } from 'lucide-react';
import { parseAsString, useQueryState } from 'nuqs';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

export function QuestionBank() {
    const orgCrmSystems = useGetOrganizationCrmSystems();
    const [checkedItems, setCheckedItems] = useState(crmRegistry.getSystemsList()?.map((item) => item.id) || []);
    const handleCheckboxChange = (id: CrmSystemIds, checked: boolean) => {
        setCheckedItems((prev) =>
        checked ? [...prev, id] : prev.filter((itemId) => itemId !== id)
        );
    };

    const { organization } = useOrganizationStore();
    // const [crmSystem, setCrmSystem] = useQueryState('crmSystem', parseAsString.withDefault('all'));

    // const [accordionValue, setAccordionValue] = useState<string>('collaborate');
    const [globalFilter, setGlobalFilter] = useQueryState('search', parseAsString.withDefault(''));
    const [showAddQuestionModal, setShowAddQuestionModal] = useState<boolean>(false);

    const { data: questionBankQuestions, isLoading: isLoadingQuestions } = useGetQuestionBankQuestions({
        crmSystem: checkedItems,
        organisationId: organization?.id,
        search: globalFilter,
    });
    // console.log('questionBankQuestions', questionBankQuestions);

    const { mutateAsync: addBankQuestion, isPending: isAddingBankQuestion } = useAddBankQuestion();
    const addQuestionHandler = async (formData: QuestionFormSchemaType) => {
        const questionBody = {
            ...formData,
            organisation_id: organization?.id,
            crm_system: 'salesforce', // TODO!
        }

        await addBankQuestion(questionBody)
            .then(() => {
                SuccessPopup('Question added successfully!');
                setShowAddQuestionModal(false);
            })
            .catch((error) => {
                console.error(error);
                const errorMessage = error?.message.includes("duplicate") ? 'A question with the same identifier already exists.' : 'Failed to add a question. Please try again later.';
                ErrorPopup(errorMessage);
                captureException(error);
            });
    }

    const recordsByCrm = useMemo(() => {
        if(!questionBankQuestions || !orgCrmSystems) return;

        const crmCounts: Record<string, number> = {};
        for (const crm of orgCrmSystems) {
            crmCounts[crm.id] = 0;
        }

        for (const record of questionBankQuestions) {
            if (record.crm_system in crmCounts) {
                crmCounts[record.crm_system]++;
            }
        }

        return crmCounts;

    }, [questionBankQuestions]);

    return (
        <>
            {(isLoadingQuestions) && (<LoaderWithBackdrop />)}

            <AddBankQuestionModal
                show={showAddQuestionModal}
                onHide={() => setShowAddQuestionModal(false)}
                onSubmit={addQuestionHandler}
                defaultStep={3}
            />

            <div className="flex flex-col flex-1">
                <NavigationBarBreadCrumbs.Set
                    currentPageName={"Manage Questions"}
                    pages={[
                            {
                            title: "Home",
                            goTo: `/welcome`
                        },
                        {
                            title: organization?.name || "-",
                            goTo: `/organization/${organization?.id}`
                        },
                        
                    ]}
                />

                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <CrmSystemCheckboxSelectSanitized 
                        checkedSystemsIds={checkedItems}
                        handleCheckboxChange={handleCheckboxChange}
                        recordsByCrm={recordsByCrm}
                    />

                    <div className="w-full sm:w-1/2 md:w-1/3">
                        <SearchInput
                            initialValue={globalFilter}
                            setValue={setGlobalFilter}
                            debounceDelay={500}
                            placeholder="Search questions by question, description or identifier"
                        />
                    </div>
                </div>

                <div className="flex-1">
                    { (!isLoadingQuestions) && (
                        <VirtualListOfQuestions
                            height="calc(100svh - 64px - 16px - 44px - 17px - 52px - 42px)"
                            questions={questionBankQuestions ?? []}
                            isLoading={isLoadingQuestions}
                        />
                    )}
                </div>
              
                <div className="flex items-center justify-end gap-2 mt-3">
                    <Button asChild size='sm' variant="secondary">
                        <Link to={`/organization/${organization?.id}`}>
                            Back
                        </Link>
                    </Button>

                    <div className="flex items-center gap-2">
                        <Button disabled={true} variant="accent" size='sm'>
                            Import Questions From File (.xlsx) (WIP) {/* import-questions */}
                        </Button>
                        <Button size='sm' onClick={() => setShowAddQuestionModal(true)} disabled={isAddingBankQuestion}>
                            {isAddingBankQuestion && (<Loader size="16" className={`animate-spin`} />)} Add Question
                        </Button>
                    </div>
                </div>
            </div>
        </>
    )
}


const VirtualListOfQuestions = ({ 
    questions, 
    height="calc(100svh - 64px - 16px - 44px - 17px - 52px - 40px - 2px)",
    isLoading = false
} : { 
    questions: TablesInsert<'question_bank'>[], 
    height?: string,
    isLoading?: boolean 
}) => {
    const parentRef = useRef<HTMLDivElement>(null)

    const rowVirtualizer = useVirtualizer({
        count: questions.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 78,
        overscan: 5,
    })

    return (
        <div ref={parentRef} className="pt-3 overflow-auto pe-3" style={{minHeight: '200px', height }}>
            <div
                style={{
                    height: rowVirtualizer.getTotalSize(),
                    position: 'relative',
                }}
                
            >
                { !isLoading && rowVirtualizer.getVirtualItems().length === 0 && (
                    <div
                        className="absolute top-0 left-0 right-0"
                        style={{
                            transform: `translateY(0px)`,
                            height: `70px`,
                        }}
                    >
                        <div className="flex items-center justify-center h-full">
                            <div className="text-sm text-center">No data available for selected CRM system</div>
                        </div>
                    </div>
                )}
                {rowVirtualizer.getVirtualItems().map(virtualRow => {
                    const question = questions[virtualRow.index]
                    return (
                        <div
                            key={question.id}
                            className="absolute top-0 left-0 right-0"
                            style={{
                                transform: `translateY(${virtualRow.start}px)`,
                                height: `${virtualRow.size}px`,
                            }}
                        >
                            <QuestionRow question={question} />
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

const QuestionRow = ({
    question
}: {
    question: TablesInsert<'question_bank'>
}) => {
    // Delete Question Logic
    const { mutateAsync: deleteBankQuestion, isPending: isPendingDeleteBankQuestion } = useDeleteBankQuestion();
    const deleteHandler = useCallback(async (id?: string) => {
        if (!id) return;

        const userDecision = await DecisionModal({
            message: 'You are about to delete a question. Are you sure you want to proceed?',
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel'
        });
        if (!userDecision.isConfirmed) return;

        await deleteBankQuestion(id)
            .then(() => {
                SuccessPopup('Question deleted successfully!');
            })
            .catch((error) => {
                console.error(error);
                const errorMessage = 'Failed to delete a question. Please try again later.';
                ErrorPopup(errorMessage);
                captureException(error);
            });
    }, [deleteBankQuestion])

    // Edit Question Logic
    const [show, setShow] = useState(false);
    const [selectedQuestionData, setSelectedQuestionData] = useState<TablesInsert<'question_bank'> | null>(null);
    const { mutateAsync: editBankQuestion, isPending: isPendingEditBankQuestion } = useEditBankQuestion();

    const showEditQuestionModal = () => {
        if (!question) return;
        setSelectedQuestionData(question);
        setShow(true);
    };

    const onCloseEditModal = () => {
        setShow(false);
    }

    const editQuestionHandler = async (formData: QuestionFormSchemaType) => {
        editBankQuestion(formData)
            .then(() => {
                SuccessPopup('Question updated successfully!');
                onCloseEditModal();
            })
            .catch((error) => {
                console.error(error);
                const errorMessage = error?.message.includes("duplicate") ? 'A question with the same identifier already exists.' : 'Failed to update a question. Please try again later.';
                ErrorPopup(errorMessage);
                captureException(error);
            });
    }

    const getCrmBrandingColor = (crm: crmSystem) => {
        const system = crmRegistry.getSystemById(crm);
        return system?.branding?.color || '#ffffff';
    }

    const isPending = isPendingDeleteBankQuestion || isPendingEditBankQuestion;

    return (
        <>
            <EditQuestionModal
                show={show}
                onHide={onCloseEditModal}
                onSubmit={editQuestionHandler}
                questionData={selectedQuestionData}
                crmSystem={question.crm_system}
            />

            <div key={question.id} className="flex items-center justify-between w-full bg-gray-100 border rounded-md gap-3 h-[70px] pe-3 overflow-hidden">
                <div className="w-2 h-full" style={{ backgroundColor: getCrmBrandingColor(question.crm_system) }}></div>

                <div className="flex items-center justify-between w-full gap-2">
                    <div className="flex items-center gap-2 grow">
                        <div className="flex flex-col items-start max-w-[300px] grow">
                            <span className="text-sm font-medium line-clamp-1">{question?.question || "-"}</span>
                            <span className="text-xs capitalize line-clamp-1">{question?.crm_system || "-"}</span>
                            <span className="text-xs text-gray-500 line-clamp-1">Created at: {formatDate(question?.created_at ?? new Date, 'M/d/y')}</span>
                        </div>
                        <div className="flex flex-1">
                            <span className={cn(`text-xs text-start text-gray-500 line-clamp-3`)}>{question?.description}</span>
                        </div>
                    </div>

                    <div className="flex gap-2">
                        <div className="flex gap-0.5">
                            {/* <Button
                                size='action'
                                onClick={(e) => {
                                    e.preventDefault();
                                    showEditQuestionModal();
                                }}
                                disabled={isPending}
                            >
                                <Pencil size={16} />
                            </Button> */}

                            <TooltipButton
                                size='action'
                                onClick={(e) => {
                                    e.preventDefault();
                                    showEditQuestionModal();
                                }}
                                disabled={isPending}
                                tooltipContent="Edit"
                            >
                                <Pencil size={16} />
                            </TooltipButton>

                            {/* <Button
                                size='action'
                                variant="destructive"
                                onClick={(e) => {
                                    e.preventDefault();
                                    deleteHandler(question.id);
                                }}
                                disabled={isPending}
                            >
                                {isPending ? (<Loader size="16" className={`animate-spin`} />) : (<Trash2 size={16} />)}
                            </Button> */}

                            <TooltipButton
                                size='action'
                                variant="destructive"
                                onClick={(e) => {
                                    e.preventDefault();
                                    deleteHandler(question.id);
                                }}
                                disabled={isPending}
                                tooltipContent="Delete"
                            >
                                {isPending ? (<Loader size="16" className={`animate-spin`} />) : (<Trash2 size={16} />)}
                            </TooltipButton>
                        </div>
                    </div>
                </div>

            </div>
        </>
    )
}
